// Live board renderer. One <g> per box in local coordinates; `setTime(t)`
// moves it to the active layout's position/scale with a transform transition
// (a reflow is a drift, not a redraw) and toggles reveal classes. Draws the
// story layer: section banners, the spine, connectors; swaps the anchor art
// on a reframe; fades retired chunks. Text boxes are dressed to the terrain
// they LANDED in (board/terrain.js); terrain is
// read at first placement and again on a reframe, never on a drift, so a box
// doesn't redress mid-meeting.
//
// The mount lives for the meeting (TT-1215, the board's twin of TT-1150
// note 2 / TT-1162 note 1): a grown series goes through `update(wall)`,
// which adds nodes for what is new — boxes, lines a chunk gained, a
// reframe's banners and anchor, connectors, spine segments — and leaves
// every node that is already on the page alone, so nothing that has been
// lettered fades in or draws itself on a second time. A remount replayed
// every line and every text box's ink on every version.

import { FONTS, PALETTE, artHref, artPiece, edge, el, hash, mountPaper, mountTrim, rng, setAttrs, textBoxDress, textBoxPaint } from './render.js';
import { mountArt } from './motifArt.js';
import { edgeKey, wallAt } from './live.js';

const CSS = `
  .box { transition: transform 1.4s cubic-bezier(.4,0,.2,1), opacity 1s ease; }
  .box.gone { opacity: 0; }
  .ln { opacity: 0; transform: translateY(4px); transition: opacity .6s ease, transform .6s ease; }
  .ln.on { opacity: 1; transform: none; }
  .ln[data-sent] { cursor: zoom-in; pointer-events: auto; }
  .sym { opacity: 0; transition: opacity 1.2s ease, transform 1.4s cubic-bezier(.4,0,.2,1); } .sym.on { opacity: 1; }
  .anchor { transition: opacity 2.5s ease; }
  .anchor-stage, .anchor-final { opacity: 0; }
  .anchor.on .anchor-stage { animation: stage-flip 1350ms ease both; }
  .anchor.on .anchor-final { animation: fade-in .6s ease both; }
  .anchor.settled .anchor-stage { animation: none; }
  .anchor.settled .anchor-final { animation: none; opacity: 1; }
  .anchor.video .anchor-final { animation: none; transition: opacity .45s ease; }
  .anchor-video { transition: opacity .45s ease; }
  .anchor-video video { display: block; width: 100%; height: 100%; mix-blend-mode: multiply; }
  .anchor.video.settled .anchor-video { opacity: 0; }
  @keyframes stage-flip { 0% { opacity: 0; } 44.4% { opacity: 1; } 55.6% { opacity: 1; } 100% { opacity: 0; } }
  @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
  .pencil { opacity: 0; transition: opacity .8s ease; } .pencil.on { opacity: .35; }
  .wash { opacity: 0; transition: opacity 1.5s ease; } .wash.on { opacity: .10; }
  .hl { opacity: 0; transition: opacity .8s ease; } .hl.on { opacity: .28; }
  .banner-hl { opacity: 0; transition: opacity .8s ease; } .banner-hl.on { opacity: .35; }
  .ink { stroke-dasharray: 4000; stroke-dashoffset: 4000; opacity: 0; }
  .ink.on { opacity: 1; animation: draw 1.6s ease-out forwards; }
  .shade { opacity: 0; transition: opacity 1.2s ease .8s; } .shade.on { opacity: .35; }
  .trim { opacity: 0; transition: opacity 1s ease 1.2s; } .trim.on { opacity: 1; }
  .conn { opacity: 0; transition: opacity .8s ease; } .conn.on { opacity: 1; }
  .spine { opacity: 0; transition: opacity 1s ease; } .spine.on { opacity: .85; }
  @keyframes draw { to { stroke-dashoffset: 0; } }
  /* a settled board (a closed meeting, a static preview) and reduced
     motion: everything at its end state at once, no intros, no drifts */
  .instant * { transition: none !important; animation: none !important; }
  .instant .ink.on { stroke-dashoffset: 0; }
  @media (prefers-reduced-motion: reduce) {
    svg * { transition: none !important; animation: none !important; }
    .ink.on { stroke-dashoffset: 0; }
  }
`;

// The anchor's intro: stage frames flip through at ANCHOR_STEP each, then
// the finished piece fades in.
const ANCHOR_STEP = 750;
const INTRO_MAX_MS = 8000;

/**
 * Mount a wall into `svg` and return `{ setTime, update }`: `setTime(t)`
 * shows the board as of clock `t`; `update(wall)` takes the wall re-solved
 * for a grown series (same ids, more of them) and adds what is new.
 *
 * opts: `boardArt` (symbol_id → { url, stages, intro }, render.js),
 * `artStyles` (the styles to draw it in: each symbol's piece tagged with
 * one of them, else its default; render.js artPiece), `intro`
 * (false: the anchor shows finished at once), `played` (a Set of frame
 * indices whose intro has run, kept by the host), `instant` (a settled
 * board, see CSS), `onSentClick(sent)` (every VERBATIM line — the sentence
 * as spoken — is a link to its sentence, as on the scene's text layer; a
 * paraphrase stays inert; one listener on the svg serves them all).
 *
 * `update` is for the SAME board grown — the ids the solver mints are
 * stable and only ever added to (a chunk retires, it is never removed) —
 * so it reconciles by adding; a different board is a new mount.
 */
export function mountLive(svg, wall, plan, opts = {}) {
  const { panel } = wall;
  const boardArt = opts.boardArt || {};
  const styles = opts.artStyles;
  const art = opts.textBoxArt || wall.textBoxArt || {}; // mood → painted piece (TT-1269), the art the wall was solved with
  const pal = { ...PALETTE, ...(plan.palette || {}) };
  const seedBase = hash(plan.title?.text || 'scene');
  const defs = mountPaper(svg, panel, pal);
  el('style', {}, defs).textContent = CSS;
  svg.classList.toggle('instant', !!opts.instant);
  if (opts.onSentClick) svg.addEventListener('click', (e) => { const sent = e.target.dataset?.sent; if (sent != null) { e.stopPropagation(); opts.onSentClick(Number(sent)); } });
  // Layers, bottom → top. Every later addition goes into its layer so a
  // reframe's anchor never lands over the ink.
  const gAnchors = el('g', {}, svg), gWash = el('g', {}, svg), gSpine = el('g', {}, svg), gConn = el('g', {}, svg), gBoxes = el('g', {}, svg), gSyms = el('g', {}, svg);

  // The anchor art, with its intro, from the piece of `boardArt[symbolId]`
  // picked for `styles` (url, stages and intro all that piece's): either
  // `stages`, earlier moments of the piece (platform `vt art-stages
  // --attach`, onto the catalog piece), which flip through in CSS (step 750 ms, in step with
  // StagedArt) and land on the finished piece, or `intro`, a clip (a
  // generated video of the piece un-drawing itself, played in reverse;
  // white ground, so it multiplies onto the paper) that plays once and
  // crossfades to the finished piece; a clip wins. Neither: the piece fades
  // in plain (TT-1163). A symbol with no art mounts no anchor. A frame in `opts.played` (the host
  // keeps it across remounts) or `opts.intro === false` shows the finished
  // piece at once. A clip that never ends (a hidden tab defers media
  // loading) settles after INTRO_MAX_MS anyway. Once the intro has run
  // (the finished piece's fade ends) the anchor is `settled`: a static
  // piece, no animation left on it — an export clone restarts CSS
  // animations from their first frame, which left the art out of the
  // capture (TT-1215).
  const played = opts.played || new Set();
  const anchors = [];
  let framesSeen = 0;
  // A frame without art (the opening sketch's notes/plain template) mounts
  // no anchor; `i` stays the frame index so `played` keys hold across remounts.
  const addAnchor = (f, i) => {
    const piece = f.anchor && artPiece(boardArt, f.anchor.symbolId, styles);
    if (!piece?.url) return;
    const box = { x: f.anchor.x - f.anchor.h / 2, y: f.anchor.y - f.anchor.h / 2, width: f.anchor.h, height: f.anchor.h, preserveAspectRatio: 'xMidYMid meet' };
    const g = el('g', { class: 'anchor', opacity: 0 }, gAnchors);
    const settle = () => g.classList.add('settled');
    const clip = opts.intro === false ? null : piece.intro || null;
    const frames = opts.intro === false || clip ? [] : piece.stages || [];
    const count = frames.length;
    let video = null;
    if (clip) {
      g.classList.add('video');
      const { x, y, width, height } = box;
      const fo = el('foreignObject', { class: 'anchor-video', x, y, width, height }, g);
      video = document.createElementNS('http://www.w3.org/1999/xhtml', 'video');
      video.muted = true; video.playsInline = true; video.preload = 'auto';
      video.src = clip;
      fo.appendChild(video);
      video.addEventListener('ended', settle); video.addEventListener('error', settle);
    }
    frames.forEach((href, k) => el('image', { class: 'anchor-stage', href, style: `animation-delay:${k * ANCHOR_STEP}ms`, ...box }, g));
    const final = el('image', { class: 'anchor-final', href: piece.url, style: `animation-delay:${count * ANCHOR_STEP}ms`, ...box }, g);
    if (!clip) final.addEventListener('animationend', settle, { once: true });
    anchors.push({ i, g, video, settle });
  };
  const addFrames = () => {
    for (; framesSeen < wall.frames.length; framesSeen++) addAnchor(wall.frames[framesSeen], framesSeen);
  };
  const showAnchor = ({ i, g, video, settle }, f) => {
    if (!g.classList.contains('on')) {
      g.classList.add('on');
      if (played.has(i) || opts.instant) settle();
      else if (video) { video.play().catch(settle); setTimeout(settle, INTRO_MAX_MS); }
      played.add(i);
    }
    g.setAttribute('opacity', f.anchor.opacity);
  };
  const hideAnchor = ({ g }) => g.setAttribute('opacity', 0);

  const N = {};
  const lineY = (ln) => ln.y - ln.px * 0.15;
  const sentOf = (ln) => (opts.onSentClick && ln.verbatim && ln.sent != null ? String(ln.sent) : undefined);
  // everything a node shows of its line: text, register, baseline, link
  const applyLine = (line, b, ln) => {
    const tx = line.node;
    Object.assign(line, { text: ln.text, px: ln.px, font: ln.font, color: ln.color, x: ln.x, y: ln.y, sent: sentOf(ln) });
    tx.textContent = ln.text;
    tx.setAttribute('font-size', ln.px); tx.setAttribute('font-family', FONTS[ln.font]);
    tx.setAttribute('fill', b.banner ? pal.ink : (pal[ln.color] || pal.ink));
    tx.setAttribute('x', ln.x); tx.setAttribute('y', lineY(ln));
    if (line.sent != null) tx.dataset.sent = line.sent; else delete tx.dataset.sent;
  };
  const sameLine = (line, ln) => line.text === ln.text && line.px === ln.px && line.font === ln.font && line.color === ln.color && line.sent === sentOf(ln);
  const addLine = (n, b, ln) => {
    const line = { t: ln.t ?? 0, node: el('text', { class: 'ln', 'letter-spacing': b.banner ? 1.5 : 0 }, n.inner) };
    applyLine(line, b, ln);
    n.lines.push(line);
  };
  // A box's lines as the wall has them now: a chunk that gained items has
  // new lines (added, to reveal at their own time); a line already on the
  // page keeps its node — and its `.on` — even if what it shows was re-set
  // (the title on a reframe) or it moved to a column (setTime drifts it).
  // Lines are slots: a re-lettered title that wraps to a different number
  // of lines shifts its date line up or down an index (TT-1266: the date
  // in the title's face, the old date left beneath the rule), so a node is
  // re-applied to the line it now holds, and nodes past the box's lines go.
  // Either way the underline is stale: `n.w = 0` makes the next resize draw it.
  const syncLines = (n, b) => {
    for (const [i, ln] of b.lines.entries()) {
      if (i >= n.lines.length) { addLine(n, b, ln); continue; }
      const line = n.lines[i];
      line.t = ln.t ?? 0;
      if (!sameLine(line, ln)) { applyLine(line, b, ln); n.w = 0; }
    }
    if (n.lines.length > b.lines.length) { for (const line of n.lines.splice(b.lines.length)) line.node.remove(); n.w = 0; }
  };

  const addBox = (b) => {
    if (b.kind === 'symbol') {
      N[b.id] = { g: el('image', { class: 'sym box', href: artHref(boardArt, b.symbolId, styles) || '', x: 0, y: 0, width: b.w, height: b.h, preserveAspectRatio: 'xMidYMid meet' }, gSyms), isSym: true };
      return;
    }
    const seed = seedBase ^ hash(b.id);
    const rand = rng(seed);
    const tilt = (rand() - 0.5) * (b.banner ? 1.6 : 2.4);
    const g = el('g', { class: 'box' }, gBoxes);
    const inner = el('g', { transform: `rotate(${tilt} ${b.w / 2} ${b.h / 2})` }, g);
    const n = { g, inner, lines: [], w: 0, h: 0, dress: null, frameIdx: null };
    // the dress is read once per landing (see setTime)
    n.dressFor = (rect, frame) => textBoxDress({ ...rect, motif: b.motif }, frame?.regions || [], seed);
    // the outline is sized to the box as laid out *now*; it is redrawn when the
    // chunk grows or its dressing changes (a landing on a new frame)
    n.resize = (w, h, dress = n.dress) => {
      if (w === n.w && h === n.h && dress === n.dress) return;
      n.w = w; n.h = h; n.dress = dress;
      const local = { x: 0, y: 0, w, h };
      inner.setAttribute('transform', `rotate(${tilt} ${w / 2} ${h / 2})`);
      n.wash?.setAttribute('rx', w * 0.6); n.wash?.setAttribute('ry', h * 0.7);
      // the same recipe as the static board (render.js); a painted piece
      // empties the paths and its tiles are redrawn to the box
      const p = textBoxPaint(dress, local, seed, pal, art);
      setAttrs(n.shade, p.shade); setAttrs(n.pencil, p.pencil); setAttrs(n.ink, p.ink);
      if (n.trim) { n.trim.replaceChildren(); if (p.trim) mountTrim(n.trim, dress, local, rng(seed + 13)); }
      if (n.art) { n.art.replaceChildren(); if (p.piece) mountArt(n.art, p.piece, local, el); }
      // the highlight (a loose line, a banner) and the title's rule run
      // under the first line as lettered now (a re-lettered title, TT-1266)
      const l0 = n.lines[0];
      if (n.hl && l0) { const hw = Math.min(w - l0.x * 2, l0.text.length * l0.px * (b.banner ? 0.6 : 0.5)); n.hl.setAttribute('d', `M${l0.x - 2},${l0.y + 2} q${hw / 2},${5} ${hw},0`); }
      if (n.rule) { const rw = l0 ? Math.min(w, l0.text.length * l0.px * 0.55) : w; n.rule.setAttribute('d', `M4,${h - 6} q${rw / 2},${8} ${rw},0`); }
    };
    if (b.kind === 'chunk') {
      const c = pal[b.hue] || pal.accent;
      if (!b.banner) n.wash = el('ellipse', { class: 'wash box', cx: 0, cy: 0, rx: b.w * 0.6, ry: b.h * 0.7, fill: c, filter: 'url(#wash)' }, gWash);
      if (b.container) {
        n.art = el('g', { class: 'art' }, inner);
        n.shade = el('path', { class: 'shade', fill: pal.shade, transform: 'translate(5,6)' }, inner);
        n.pencil = el('path', { class: 'pencil', fill: '#ffffff', 'fill-opacity': 0.9, stroke: pal.shade, 'stroke-width': 1.5, 'stroke-linejoin': 'round' }, inner);
        n.ink = el('path', { class: 'ink', fill: '#ffffff', 'fill-opacity': 0.72, stroke: pal.ink, 'stroke-width': 2, 'stroke-linejoin': 'round' }, inner);
        n.trim = el('g', { class: 'trim' }, inner);
      } else if (b.lines.length) {
        n.hl = el('path', { class: b.banner ? 'banner-hl' : 'hl', stroke: b.banner ? pal.accent2 : c, 'stroke-width': b.lines[0].px * (b.banner ? 0.55 : 0.45), fill: 'none', 'stroke-linecap': 'round' }, inner);
      }
    }
    syncLines(n, b);
    if (b.kind === 'title') n.rule = el('path', { stroke: pal.accent, 'stroke-width': 5, fill: 'none', 'stroke-linecap': 'round', opacity: 0.8 }, inner);
    N[b.id] = n; // sized by the first setTime, which every host calls at once
  };

  // Edges are the last version's — the spine may drop a segment, a review
  // may withdraw a connector (TT-1266) — so both are keyed and reconciled.
  const connNodes = new Map(), spineNodes = new Map();
  let onSpine = new Set();
  const syncEdgeSet = (nodes, edges, make) => {
    const keep = new Set(edges.map(edgeKey));
    for (const [k, e] of nodes) if (!keep.has(k)) { e.node.remove(); nodes.delete(k); }
    for (const e of edges) {
      const k = edgeKey(e);
      if (nodes.has(k)) nodes.get(k).e = e; else nodes.set(k, { e, node: make(e) });
    }
  };
  const syncEdges = () => {
    syncEdgeSet(connNodes, wall.connectors, (c) => el('path', { class: 'conn', fill: 'none', stroke: pal.ink, 'stroke-width': 3, 'stroke-linecap': 'round',
      'stroke-dasharray': c.style === 'dashed' ? '10 8' : 'none', 'marker-end': c.arrow ? 'url(#arrow)' : 'none' }, gConn));
    // a connector on a step the spine draws is the same arrow twice (TT-1237;
    // the planner drops these now, older plans still carry them): the spine wins
    onSpine = new Set((wall.spineSegs || []).flatMap(s => [edgeKey(s), edgeKey({ from: s.to, to: s.from })]));
    syncEdgeSet(spineNodes, wall.spineSegs || [], () => el('path', { class: 'spine', fill: 'none', stroke: pal.accent, 'stroke-width': 7, 'stroke-linecap': 'round', 'marker-end': 'url(#arrow-spine)' }, gSpine));
  };

  // Bring the DOM up to the wall: frames, boxes (new ones added, known
  // ones' lines synced), edges.
  const sync = () => {
    addFrames();
    for (const b of wall.boxes) {
      if (!N[b.id]) addBox(b);
      else if (!N[b.id].isSym) syncLines(N[b.id], b);
    }
    syncEdges();
  };
  sync();

  // A curve from A to B that crosses nothing else: try a bulge away from the
  // board centre first, then the other side, then wider, then straight. If
  // every route cuts through unrelated content, no arrow — a scribe would not
  // draw one either. Adjacent boxes get no arrow (no room for a tail).
  const MIN_LEN = 40;
  const bez = (P0, C, P1, u) => [(1 - u) ** 2 * P0[0] + 2 * (1 - u) * u * C[0] + u ** 2 * P1[0], (1 - u) ** 2 * P0[1] + 2 * (1 - u) * u * C[1] + u ** 2 * P1[1]];
  const inRect = (p, r, pad) => p[0] > r.x - pad && p[0] < r.x + r.w + pad && p[1] > r.y - pad && p[1] < r.y + r.h + pad;
  const route = (from, to, rect, k0 = 0.18) => {
    const A = rect[from], B = rect[to];
    const ca = [A.x + A.w / 2, A.y + A.h / 2], cb = [B.x + B.w / 2, B.y + B.h / 2];
    const [sx0, sy0] = edge(A, ...cb), [sx1, sy1] = edge(B, ...ca);
    if (Math.hypot(sx1 - sx0, sy1 - sy0) < MIN_LEN) return null;
    const mine = id => id === from || id === to || wall.byId[id]?.of === from || wall.byId[id]?.of === to;
    const obstacles = Object.keys(rect).filter(id => !mine(id)).map(id => rect[id]);
    const mx0 = (sx0 + sx1) / 2, my0 = (sy0 + sy1) / 2;
    const away = Math.sign((mx0 - panel.w / 2) * (sy1 - sy0) - (my0 - panel.h / 2) * (sx1 - sx0)) || 1;
    const PAD = 16;   // boxes are tilted and drop-shadowed: keep well clear
    for (const k of [k0, -k0, 0, k0 * 1.5, -k0 * 1.5, k0 * 2, -k0 * 2, k0 * 3, -k0 * 3, k0 * 4.5, -k0 * 4.5, k0 * 6, -k0 * 6]) {
      const C = [mx0 + (sy1 - sy0) * k * away, my0 - (sx1 - sx0) * k * away];
      // leave/enter on the faces that look toward the curve, so the arrow
      // takes the natural side rather than squeezing into a gap
      const P0 = k ? edge(A, ...C) : [sx0, sy0], P1 = k ? edge(B, ...C) : [sx1, sy1];
      if (Math.hypot(P1[0] - P0[0], P1[1] - P0[1]) < MIN_LEN) continue;
      let clear = true;
      for (let i = 0; i <= 24 && clear; i++) if (obstacles.some(r => inRect(bez(P0, C, P1, i / 24), r, PAD))) clear = false;
      if (clear) return `M${P0[0]},${P0[1]} Q${C[0]},${C[1]} ${P1[0]},${P1[1]}`;
    }
    return null;
  };
  // first placement is instant; every later move is a drift
  const put = (n, x, y, s) => {
    const fresh = !n.on; n.on = true;
    if (fresh) n.g.style.transition = 'none';
    n.g.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
    if (fresh) { n.g.getBoundingClientRect(); n.g.style.transition = ''; }
  };

  // routes depend only on the layout and which boxes are visible — cache per that key
  let routeKey = null;
  function setTime(t) {
    const view = wallAt(wall, t);
    const frameIdx = wall.frames.indexOf(view.frame);
    const seen = new Set(), rect = {};
    for (const b of view.boxes) {
      seen.add(b.id);
      const n = N[b.id]; if (!n) continue;
      const s = b.scale;
      rect[b.id] = { x: b.x, y: b.y, w: b.w * s, h: b.h * s };
      put(n, b.x, b.y, s);
      n.g.classList.remove('gone');
      if (n.isSym) { n.g.classList.add('on'); continue; }
      // a landing (first placement, or the first tick of a new frame) reads the terrain; a drift keeps it
      if (n.frameIdx !== frameIdx) { n.frameIdx = frameIdx; n.resize(b.w, b.h, n.dressFor(rect[b.id], view.frame)); } else n.resize(b.w, b.h);
      if (n.wash) { n.wash.setAttribute('transform', `translate(${b.x + b.w * s / 2},${b.y + b.h * s / 2}) scale(${s})`); n.wash.classList.add('on'); }
      for (const [i, ln] of n.lines.entries()) {
        const src = b.allLines?.[i];
        if (src && (src.x !== ln.x || src.y !== ln.y)) { ln.x = src.x; ln.y = src.y; ln.node.setAttribute('x', src.x); ln.node.setAttribute('y', lineY(ln)); }
        // a line the active layout does not carry (added by a version the
        // solver left off) waits for one that does: shown now, it would sit
        // where a column the layout never sized put it, off the card or the
        // board (TT-1266)
        ln.node.classList.toggle('on', ln.t <= t && i < b.allLines.length);
      }
      n.pencil?.classList.toggle('on', !b.inked);
      n.ink?.classList.toggle('on', b.inked);
      n.shade?.classList.toggle('on', b.inked);
      n.trim?.classList.toggle('on', b.inked);
      n.hl?.classList.toggle('on', b.inked);
    }
    for (const id in N) if (!seen.has(id)) {
      const n = N[id];
      n.on = false; n.frameIdx = null; // off the board: its next appearance is a landing
      if (n.isSym) { n.g.classList.remove('on'); continue; }
      const retired = wall.byId[id]?.tRetire != null && wall.byId[id].tRetire <= t;
      if (retired) { n.g.classList.add('gone'); n.wash?.classList.remove('on'); continue; }
      for (const ln of n.lines) ln.node.classList.remove('on');
      n.pencil?.classList.remove('on'); n.ink?.classList.remove('on'); n.shade?.classList.remove('on'); n.trim?.classList.remove('on'); n.hl?.classList.remove('on'); n.wash?.classList.remove('on');
    }
    for (const a of anchors) if (a.i === frameIdx) showAnchor(a, view.frame); else hideAnchor(a);
    const key = wall.layouts.indexOf(view.layout) + ':' + [...seen].join(',');
    const reroute = key !== routeKey;
    routeKey = key;
    const routeEdges = (nodes, k0, shown = () => true) => {
      for (const { e, node } of nodes.values()) {
        const on = e.t <= t && rect[e.from] && rect[e.to] && shown(e);
        if (on && reroute) node.dataset.d = route(e.from, e.to, rect, k0) || '';
        const d = on ? node.dataset.d : '';
        node.classList.toggle('on', !!d);
        if (d) node.setAttribute('d', d);
      }
    };
    routeEdges(spineNodes, 0.22);
    routeEdges(connNodes, undefined, (c) => !onSpine.has(edgeKey(c)));
    return view;
  }

  // The wall re-solved for a grown series: what is new gets a node, what
  // is on the page stays as it is; the next setTime places and reveals.
  function update(next) {
    wall = next;
    routeKey = null;
    sync();
  }

  return { setTime, update };
}
